import { useMemo, useState } from 'react'
import {
  Accordion,
  ActionIcon,
  Badge,
  Button,
  Card,
  Center,
  Container,
  Group,
  Loader,
  Progress,
  Select,
  SimpleGrid,
  Stack,
  Text,
  TextInput,
  Title,
  Tooltip,
} from '@mantine/core'
import { IconAlertTriangle, IconBuildingStore, IconClipboardList, IconPencil, IconPlus, IconSearch } from '@tabler/icons-react'
import { useQuery } from '@tanstack/react-query'
import { Link, useSearchParams } from 'react-router-dom'
import type { ProjectDto } from '@ledokol/contracts'
import { fetchProjects } from '../shared/api/projects'
import { fetchDepartments } from '../shared/api/departments'
import { fetchFunnelSegments } from '../shared/api/funnel-segments'
import { fetchUsers } from '../shared/api/users'
import { fetchAdvertisers } from '../shared/api/advertisers'
import { AppLayout } from '../features/projects/app-shell'
import { CreateAdvertiserModal, CreateProjectModal, RenameModal } from '../features/projects/project-dialogs'
import { useAuthStore } from '../features/auth/store'

const STATUS_LABEL: Record<string, { label: string; color: string }> = {
  active: { label: 'Активен', color: 'green' },
  pause: { label: 'Пауза', color: 'yellow' },
  finished: { label: 'Завершён', color: 'gray' },
  archive: { label: 'Архив', color: 'gray' },
}

/**
 * Компактная карточка проекта/под-проекта (#3) — плотнее, чтобы на экран влезало
 * заметно больше рекламодателей. Ключевая инфа сохранена: название, отделы/сегмент,
 * лид, метрики Открыто/Просрочка/Готово.
 */
function ProjectCard({ p }: { p: ProjectDto }) {
  return (
    <Card
      withBorder
      p="xs"
      radius="md"
      component={Link}
      to={`/projects/${p.id}`}
      style={{
        cursor: 'pointer',
        borderLeft: `3px solid ${p.accent_color ?? '#1F4E79'}`,
        transition: 'transform .12s ease, box-shadow .12s ease',
      }}
      className="lk-project-card"
    >
      <Stack gap={6}>
        <Group justify="space-between" wrap="nowrap" align="flex-start" gap="xs">
          <Text fw={700} size="sm" truncate style={{ minWidth: 0 }}>
            {p.name}
          </Text>
          <Badge color={STATUS_LABEL[p.status]?.color ?? 'gray'} variant="light" size="xs">
            {STATUS_LABEL[p.status]?.label ?? p.status}
          </Badge>
        </Group>

        <Group gap={4} wrap="wrap">
          {p.departments.map((d) => (
            <Badge key={d.id} variant="light" size="xs" color="blue">
              {d.name}
            </Badge>
          ))}
          {p.segment && (
            <Badge size="xs" style={{ background: p.segment.color, color: '#fff' }}>
              {p.segment.name}
            </Badge>
          )}
        </Group>

        <Text size="xs" c="dimmed" truncate>
          Лид: <Text span fw={500} c="dark">{p.lead.full_name}</Text>
        </Text>

        <Group justify="space-between" gap={0}>
          <Stack gap={0} align="center" style={{ flex: 1 }}>
            <Text size="md" fw={700} lh={1.1}>{p.open_tasks}</Text>
            <Text size="10px" c="dimmed">Открыто</Text>
          </Stack>
          <Stack gap={0} align="center" style={{ flex: 1 }}>
            <Group gap={2}>
              <Text size="md" fw={700} lh={1.1} c={p.overdue_tasks > 0 ? 'red.7' : 'dimmed'}>{p.overdue_tasks}</Text>
              {p.overdue_tasks > 0 && <IconAlertTriangle size={12} color="var(--mantine-color-red-7)" />}
            </Group>
            <Text size="10px" c="dimmed">Просрочка</Text>
          </Stack>
          <Stack gap={0} align="center" style={{ flex: 1 }}>
            <Text size="md" fw={700} lh={1.1} c="green.7">{p.done_30d}</Text>
            <Text size="10px" c="dimmed">Готово·30д</Text>
          </Stack>
        </Group>

        <Progress
          value={p.open_tasks + p.done_30d > 0 ? (p.done_30d / (p.open_tasks + p.done_30d)) * 100 : 0}
          color="green"
          size={4}
        />
      </Stack>
    </Card>
  )
}

interface AdvertiserGroup {
  id: string
  name: string
  projects: ProjectDto[]
  open: number
  overdue: number
  done: number
}

/**
 * Группировка списка: проекты с advertiser=null — standalone (карточка как сегодня);
 * проекты одного рекламодателя сворачиваются в одну разворачиваемую строку (топики/группы).
 */
function splitByAdvertiser(
  projects: ProjectDto[],
  emptyAdvertisers: { id: string; name: string }[],
): { standalone: ProjectDto[]; groups: AdvertiserGroup[] } {
  const standalone: ProjectDto[] = []
  const byAdv = new Map<string, AdvertiserGroup>()
  // Рекламодатель без проектов (заведён без ТГ, проекты ещё не добавлены) — пустая группа.
  for (const a of emptyAdvertisers) byAdv.set(a.id, { id: a.id, name: a.name, projects: [], open: 0, overdue: 0, done: 0 })
  for (const p of projects) {
    if (!p.advertiser) {
      standalone.push(p)
      continue
    }
    const g = byAdv.get(p.advertiser.id) ?? {
      id: p.advertiser.id,
      name: p.advertiser.name,
      projects: [],
      open: 0,
      overdue: 0,
      done: 0,
    }
    g.projects.push(p)
    g.open += p.open_tasks
    g.overdue += p.overdue_tasks
    g.done += p.done_30d
    byAdv.set(p.advertiser.id, g)
  }
  const groups = [...byAdv.values()].sort((a, b) => a.name.localeCompare(b.name, 'ru'))
  return { standalone, groups }
}

export function ProjectsPage() {
  const [params, setParams] = useSearchParams()
  const departmentId = params.get('department') ?? ''
  const segmentId = params.get('segment') ?? ''
  const employeeId = params.get('employee') ?? ''
  const [search, setSearch] = useState('')

  const { data: projects, isLoading, error } = useQuery({
    queryKey: ['projects', departmentId, segmentId, employeeId],
    queryFn: () =>
      fetchProjects({
        department_id: departmentId || undefined,
        segment_id: segmentId || undefined,
        employee: employeeId || undefined,
      }),
  })
  const { data: depts } = useQuery({ queryKey: ['departments'], queryFn: fetchDepartments })
  const { data: segments } = useQuery({ queryKey: ['funnel-segments'], queryFn: fetchFunnelSegments })
  // Сотрудники для фильтра — активные, не-стабы (видны всем ролям: GET /users без права).
  const { data: users } = useQuery({ queryKey: ['users', 'active'], queryFn: () => fetchUsers({ active: true }) })
  const currentDept = depts?.find((d) => d.id === departmentId)
  const me = useAuthStore((st) => st.user)
  const canManageAdvertisers = me?.permissions.includes('projects.manage') ?? false
  const canCreateProject = me?.permissions.includes('projects.create') ?? false
  const [createAdvOpen, setCreateAdvOpen] = useState(false)
  const [createIn, setCreateIn] = useState<{ id: string; name: string } | null>(null)
  const [renaming, setRenaming] = useState<{ kind: 'advertiser'; id: string; name: string } | null>(null)
  // Пустые рекламодатели (0 проектов) показываем только без фильтров отдела/сегмента/сотрудника:
  // с фильтром они — шум (у них нет проектов, попадающих под фильтр).
  const unfiltered = !departmentId && !segmentId && !employeeId
  const { data: advertisers } = useQuery({ queryKey: ['advertisers'], queryFn: fetchAdvertisers, enabled: unfiltered })

  function setParam(key: string, v: string | null) {
    const next = new URLSearchParams(params)
    if (v) next.set(key, v)
    else next.delete(key)
    setParams(next, { replace: true })
  }

  // Поиск — клиентский, мгновенный, по названию проекта И рекламодателя (поверх
  // отскоупленных и серверно-отфильтрованных данных, ≤500 — компактно).
  const filtered = useMemo(() => {
    if (!projects) return []
    const q = search.trim().toLowerCase()
    if (!q) return projects
    return projects.filter(
      (p) => p.name.toLowerCase().includes(q) || (p.advertiser?.name.toLowerCase().includes(q) ?? false),
    )
  }, [projects, search])

  const emptyAdvertisers = useMemo(() => {
    if (!unfiltered) return []
    const q = search.trim().toLowerCase()
    return (advertisers ?? []).filter((a) => a.project_count === 0 && (!q || a.name.toLowerCase().includes(q)))
  }, [advertisers, unfiltered, search])
  const { standalone, groups } = splitByAdvertiser(filtered, emptyAdvertisers)

  return (
    <AppLayout>
      <Container size="xl" py="xl">
        <Stack gap="md" mb="xl">
          <Group justify="space-between" align="center">
            <Title order={2}>Рекламодатели{currentDept ? ` · ${currentDept.name}` : ''}</Title>
            {canManageAdvertisers && (
              <Button leftSection={<IconPlus size={16} />} onClick={() => setCreateAdvOpen(true)}>
                Добавить рекламодателя
              </Button>
            )}
          </Group>
          <Text c="dimmed" size="sm">
            {currentDept
              ? `Проекты отдела «${currentDept.name}». Очистить фильтр — «Все» в сайдбаре.`
              : 'Проекты, к которым у вас есть доступ.'}
          </Text>
          <Group gap="sm" align="flex-end" wrap="wrap">
            <TextInput
              placeholder="Поиск по названию / рекламодателю"
              leftSection={<IconSearch size={16} />}
              value={search}
              onChange={(e) => setSearch(e.currentTarget.value)}
              w={300}
            />
            <Select
              placeholder="Сотрудник (лид или участник)"
              data={(users ?? []).map((u) => ({ value: u.id, label: u.full_name }))}
              value={employeeId || null}
              onChange={(v) => setParam('employee', v)}
              searchable
              clearable
              w={260}
            />
            {segments && segments.length > 0 && (
              <Select
                placeholder="Сегмент воронки"
                data={segments.map((s) => ({ value: s.id, label: s.name }))}
                value={segmentId || null}
                onChange={(v) => setParam('segment', v)}
                clearable
                w={220}
              />
            )}
          </Group>
        </Stack>

        {isLoading && (
          <Center mih={200}>
            <Loader />
          </Center>
        )}

        {error && <Text c="red">Не удалось загрузить проекты. Попробуйте обновить страницу.</Text>}

        {projects && projects.length === 0 && groups.length === 0 && (
          <Card withBorder p="xl">
            <Center>
              <Stack align="center" gap="xs">
                <IconClipboardList size={48} color="var(--mantine-color-gray-5)" />
                <Text c="dimmed">
                  {employeeId ? 'Нет проектов с этим сотрудником в зоне вашего доступа' : 'У вас пока нет доступа к проектам'}
                </Text>
              </Stack>
            </Center>
          </Card>
        )}

        {projects && projects.length > 0 && filtered.length === 0 && groups.length === 0 && (
          <Text c="dimmed" py="md">Ничего не найдено по запросу «{search.trim()}».</Text>
        )}

        {(filtered.length > 0 || groups.length > 0) && (
          <Stack gap="md">
            {/* Крупные рекламодатели: одна строка → разворот в под-проекты (топики/группы). */}
            {groups.length > 0 && (
              <Accordion variant="separated" multiple chevronPosition="left">
                {groups.map((g) => (
                  <Accordion.Item key={g.id} value={g.id}>
                    {/* Действия — рядом с Control, не внутри: Control = <button>, вложенные кнопки невалидны. */}
                    <Group wrap="nowrap" gap={0} pr="xs">
                      <Accordion.Control icon={<IconBuildingStore size={20} color="var(--mantine-color-blue-6)" />} style={{ flex: 1 }}>
                        <Group justify="space-between" wrap="nowrap" pr="md">
                          <Text fw={700}>{g.name}</Text>
                          <Group gap="xs">
                            <Badge variant="light" color="gray">
                              {g.projects.length} проект(ов)
                            </Badge>
                            <Badge variant="light" color="blue">
                              {g.open} открыто
                            </Badge>
                            {g.overdue > 0 && (
                              <Badge variant="light" color="red">
                                {g.overdue} просрочка
                              </Badge>
                            )}
                            <Badge variant="light" color="green">
                              {g.done} готово · 30д
                            </Badge>
                          </Group>
                        </Group>
                      </Accordion.Control>
                      {canCreateProject && (
                        <Tooltip label="Добавить проект">
                          <ActionIcon variant="subtle" aria-label={`Добавить проект в «${g.name}»`} onClick={() => setCreateIn({ id: g.id, name: g.name })}>
                            <IconPlus size={16} />
                          </ActionIcon>
                        </Tooltip>
                      )}
                      {canManageAdvertisers && (
                        <Tooltip label="Переименовать">
                          <ActionIcon variant="subtle" color="gray" aria-label={`Переименовать «${g.name}»`} onClick={() => setRenaming({ kind: 'advertiser', id: g.id, name: g.name })}>
                            <IconPencil size={16} />
                          </ActionIcon>
                        </Tooltip>
                      )}
                    </Group>
                    <Accordion.Panel>
                      {g.projects.length === 0 && <Text c="dimmed" size="sm">Проектов пока нет.</Text>}
                      <SimpleGrid cols={{ base: 1, sm: 2, md: 3, lg: 4 }} spacing="sm">
                        {g.projects.map((p) => (
                          <ProjectCard key={p.id} p={p} />
                        ))}
                      </SimpleGrid>
                    </Accordion.Panel>
                  </Accordion.Item>
                ))}
              </Accordion>
            )}

            {/* Обычные проекты (без рекламодателя) — компактная сетка, больше колонок (#3). */}
            {standalone.length > 0 && (
              <SimpleGrid cols={{ base: 1, xs: 2, sm: 3, md: 4, lg: 5 }} spacing="sm">
                {standalone.map((p) => (
                  <ProjectCard key={p.id} p={p} />
                ))}
              </SimpleGrid>
            )}
          </Stack>
        )}
      </Container>
      <CreateAdvertiserModal opened={createAdvOpen} onClose={() => setCreateAdvOpen(false)} />
      <CreateProjectModal advertiser={createIn} onClose={() => setCreateIn(null)} />
      <RenameModal target={renaming} onClose={() => setRenaming(null)} />
    </AppLayout>
  )
}
